package com.mgacreative.hotelru.feature.admin.ui

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Person
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AdminStaffManagementScreen(
    viewModel: AdminStaffViewModel = koinViewModel(),
    onNavigateBack: () -> Unit
) {
    val isLoading by viewModel.isLoading.collectAsState()
    val errorMessage by viewModel.errorMessage.collectAsState()
    val successMessage by viewModel.successMessage.collectAsState()

    var showAddDialog by remember { mutableStateOf(false) }

    Scaffold(
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        topBar = {
            TopAppBar(
                title = { Text(l10n("Personel Yönetimi")) },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(Icons.Default.Close, contentDescription = l10n("Back"))
                    }
                },
                actions = {
                    Button(onClick = { showAddDialog = true }) {
                        Icon(Icons.Default.Add, contentDescription = null)
                        Spacer(Modifier.width(8.dp))
                        Text(l10n("Yeni Personel"))
                    }
                }
            )
        }
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues),
            contentAlignment = Alignment.Center
        ) {
            Column(
                modifier = Modifier.fillMaxSize().padding(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (errorMessage != null) {
                    Text(text = errorMessage!!, color = MaterialTheme.colorScheme.error)
                    Spacer(modifier = Modifier.height(8.dp))
                }
                if (successMessage != null) {
                    Text(text = successMessage!!, color = MaterialTheme.colorScheme.primary)
                    Spacer(modifier = Modifier.height(8.dp))
                }
                
                val staffList by viewModel.staffList.collectAsState()

                if (staffList.isEmpty()) {
                    Text(l10n("Henüz sisteme kayıtlı personel bulunmuyor."), style = MaterialTheme.typography.bodyMedium)
                } else {
                    val listState = androidx.compose.foundation.lazy.rememberLazyListState()
                    LazyColumn(
                        state = listState,
                        modifier = Modifier.fillMaxSize().verticalScrollbar(listState)
                    ) {
                        items(staffList) { staff ->
                            Card(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(vertical = 4.dp),
                                elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
                            ) {
                                Row(
                                    modifier = Modifier.padding(16.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Icon(Icons.Default.Person, contentDescription = "Staff", tint = MaterialTheme.colorScheme.primary)
                                    Spacer(modifier = Modifier.width(16.dp))
                                    Column(modifier = Modifier.weight(1f)) {
                                        Text(staff.email ?: staff.full_name ?: staff.id, style = MaterialTheme.typography.bodyLarge)
                                        Text(if (staff.roles.isEmpty()) l10n("Rol belirtilmedi") else staff.roles.map { l10n(it) }.joinToString(", "), style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.secondary)
                                    }
                                    if (!staff.member_code.isNullOrBlank()) {
                                        Surface(
                                            color = MaterialTheme.colorScheme.primaryContainer,
                                            shape = MaterialTheme.shapes.small
                                        ) {
                                            Text(
                                                text = staff.member_code,
                                                style = MaterialTheme.typography.labelSmall,
                                                color = MaterialTheme.colorScheme.onPrimaryContainer,
                                                modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        if (showAddDialog) {
            AddStaffDialog(
                onDismiss = {
                    showAddDialog = false
                    viewModel.clearMessages()
                },
                onConfirm = { username, password, memberCode, dept, roles ->
                    viewModel.createStaff(username, password, dept, roles, memberCode)
                    showAddDialog = false
                }
            )
        }
        
        if (isLoading) {
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
        }
    }
}

@Composable
fun AddStaffDialog(
    onDismiss: () -> Unit,
    onConfirm: (String, String, String?, String?, List<String>) -> Unit
) {
    var username by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var memberCode by remember { mutableStateOf("") }
    var selectedRoles by remember { mutableStateOf(setOf<String>()) }
    
    val availableRoles = listOf("FRONT_DESK", "HOUSEKEEPING", "RESTAURANT", "ACCOUNTING", "MANAGER")
    
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(20.dp),
        title = { Text(l10n("Yeni Personel Ekle")) },
        text = {
            Column(modifier = Modifier.fillMaxWidth()) {
                OutlinedTextField(
                    value = username,
                    onValueChange = { username = it },
                    label = { Text(l10n("Kullanıcı Adı")) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(8.dp))
                OutlinedTextField(
                    value = password,
                    onValueChange = { password = it },
                    label = { Text(l10n("Şifre")) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(8.dp))
                OutlinedTextField(
                    value = memberCode,
                    onValueChange = { memberCode = it.uppercase() },
                    label = { Text(l10n("Üye Kodu (Opsiyonel)")) },
                    placeholder = { Text("Boşsa otomatik üretilir") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(16.dp))
                Text(l10n("Roller"), style = MaterialTheme.typography.titleSmall)
                Spacer(modifier = Modifier.height(8.dp))
                
                availableRoles.forEach { role ->
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Checkbox(
                            checked = selectedRoles.contains(role),
                            onCheckedChange = { checked ->
                                if (checked) {
                                    selectedRoles = selectedRoles + role
                                } else {
                                    selectedRoles = selectedRoles - role
                                }
                            }
                        )
                        Text(l10n(role))
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = { onConfirm(username, password, memberCode.ifBlank { null }, null, selectedRoles.toList()) },
                enabled = username.isNotBlank() && password.isNotBlank() && selectedRoles.isNotEmpty()
            ) {
                Text(l10n("Kaydet"))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(l10n("İptal"))
            }
        }
    )
}

